/**
 * Svelte action — geste swipe-down sur un bottom sheet pour le fermer.
 *
 * À appliquer sur le wrapper `.sheet` du modal. Le geste suit le doigt
 * (translateY), et au release :
 *   - dy > seuil OU vélocité forte → onClose() + animation jusqu'à hors-écran
 *   - sinon                          → snap-back animé
 *
 * Verrou : on ne capture le geste QUE si le touch démarre sur la zone
 * "header" (pas dans le body scrollable, sinon on hijack le scroll). En
 * pratique on tag les éléments header avec data-dismiss-handle, et le
 * touchstart est validé seulement si l'event.target est descendant.
 *
 * Usage :
 *   <div class="sheet" use:dismissSheet={{ onClose: closeFunc }}>
 *     <div class="grabber" data-dismiss-handle></div>
 *     <header data-dismiss-handle>...</header>
 *     <div class="body">...</div>  <!-- pas data-dismiss-handle, scroll normal -->
 *   </div>
 */

import { haptic } from "./haptic";

export interface DismissSheetOptions {
  onClose: () => void;
  enabled?: boolean;
}

const COMPLETE_THRESHOLD_RATIO = 0.25;
const VELOCITY_THRESHOLD = 0.6; // px/ms
const SLIDE_OUT_MS = 220;
const SNAP_BACK_MS = 200;
const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

export function dismissSheet(node: HTMLElement, options: DismissSheetOptions) {
  let opts = options;
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let active = false;
  let aborted = false;
  let currentDy = 0;

  function setTransform(y: number) {
    node.style.transform = y === 0 ? "" : `translateY(${y}px)`;
  }

  function animateTo(y: number, ms: number, then?: () => void) {
    node.style.transition = `transform ${ms}ms ${EASING}`;
    setTransform(y);
    window.setTimeout(() => {
      node.style.transition = "";
      then?.();
    }, ms);
  }

  function startsOnHandle(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return !!target.closest("[data-dismiss-handle]");
  }

  function onTouchStart(e: TouchEvent) {
    if (opts.enabled === false) return;
    if (e.touches.length !== 1) return;
    if (!startsOnHandle(e.target)) return;
    const t = e.touches[0];
    startX = t.clientX;
    startY = t.clientY;
    startTime = Date.now();
    active = true;
    aborted = false;
    currentDy = 0;
    node.style.transition = "";
  }

  function onTouchMove(e: TouchEvent) {
    if (!active || aborted) return;
    const t = e.touches[0];
    const dy = t.clientY - startY;
    const dx = Math.abs(t.clientX - startX);

    // Verrou directionnel : si le mouvement est plus horizontal que vertical, abort
    if (dy < 6 && dx > 6) {
      aborted = true;
      animateTo(0, SNAP_BACK_MS);
      return;
    }
    if (dy <= 0) {
      currentDy = 0;
      setTransform(0);
      return;
    }
    currentDy = dy;
    setTransform(dy);
    if (e.cancelable && dy > 6) e.preventDefault();
  }

  function onTouchEnd() {
    if (!active) return;
    active = false;
    if (aborted) return;
    const dt = Date.now() - startTime;
    const velocity = currentDy / Math.max(dt, 1);
    const passed =
      currentDy > window.innerHeight * COMPLETE_THRESHOLD_RATIO ||
      velocity > VELOCITY_THRESHOLD;

    if (passed) {
      haptic("light");
      animateTo(window.innerHeight, SLIDE_OUT_MS, () => {
        opts.onClose();
        // Reset transform après que le sheet soit unmount par le parent.
        // Si le composant reste monté (ex. v-if false), on reset quand même
        // pour qu'à la prochaine ouverture il parte d'en haut.
        requestAnimationFrame(() => setTransform(0));
      });
    } else {
      animateTo(0, SNAP_BACK_MS);
    }
  }

  node.addEventListener("touchstart", onTouchStart, { passive: true });
  node.addEventListener("touchmove", onTouchMove, { passive: false });
  node.addEventListener("touchend", onTouchEnd, { passive: true });
  node.addEventListener("touchcancel", onTouchEnd, { passive: true });

  return {
    update(newOpts: DismissSheetOptions) {
      opts = newOpts;
    },
    destroy() {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    },
  };
}
